import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { PrinterOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import { getScheduleImpactForMaterial } from "services/schedule-versions-services";
import { ProjectContext } from "context/ProjectProvider";
import { AppLogoIcon } from "components/svg-icons";
import { ErrorMessages } from "../../../../constants";
import { ImpactSummaryDataType } from "../compare-schedule-2/model";
import ScheduleImpactSummary from "../compare-schedule-2/schedule-impact-summary";
import {
  flattenSubmittalsForGrid,
  flattenMaterialsForGrid
} from "./grid-utils";
import {
  formatLinkedSubmittalDataForGoverningMaterial,
  getFormattedMaterialImpactData
} from "./utils";
import { TMaterialImpactAnalysis } from "./models";
import ImpactedSubmittalsGrid from "./impacted-submittals-grid";
import ImpactedMaterialsGrid from "./impacted-materials-grid";
import { DEFAULT_IMPACT_SUMMARY } from "../compare-schedule-2";

const defaultImpactAnalysisData: TMaterialImpactAnalysis = {
  HIGH: {},
  MEDIUM: {},
  LOW: {},
  DELETED: {}
} as TMaterialImpactAnalysis;

const defaultSummary: ImpactSummaryDataType = DEFAULT_IMPACT_SUMMARY;

function ScheduleImpactExportPDF() {
  const [sia, setSia] = useState(defaultImpactAnalysisData);
  const [mia, setMia] = useState(defaultImpactAnalysisData);
  const [scheduleImpactSummary, setScheduleImpactSummary] =
    useState<ImpactSummaryDataType>(defaultSummary);
  const [isLoading, setIsLoading] = useState(true);

  const { isFeatureFlagEnabled, projectToken } = useContext(ProjectContext);
  const isAffectedMaterialEnabled = isFeatureFlagEnabled("MATERIAL_IMPACT");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const importLogId = searchParams.get("importLogId") || "";
  const sourceLogId = searchParams.get("sourceLogId") || "";
  const tab = searchParams.get("tab") || ""; // "submittals", "materials", or "" (both)

  const showSubmittals = tab !== "materials";
  const showMaterials = tab !== "submittals";

  useEffect(() => {
    if (!importLogId || !sourceLogId || !projectToken) return undefined;

    const controller = new AbortController();
    const fetchData = async () => {
      try {
        const res: any = await getScheduleImpactForMaterial(
          importLogId,
          sourceLogId,
          projectToken,
          controller.signal
        );
        if (controller.signal.aborted) return;

        if (res.data) {
          const responseBody = res.data;

          const submittalImpactAnalysis =
            responseBody.success?.impactAnalysis?.submittals ||
            defaultImpactAnalysisData;

          // `|| defaultImpactAnalysisData` falls back to a non-null object constant,
          // so submittalImpactAnalysis is always truthy: the else-branch is unreachable.
          /* v8 ignore else */
          if (submittalImpactAnalysis) {
            const allMaterialDateBlock =
              responseBody.success.all_material_date_block;
            const formattedSubmittalImpactData =
              formatLinkedSubmittalDataForGoverningMaterial(
                submittalImpactAnalysis,
                allMaterialDateBlock
              );
            setSia(formattedSubmittalImpactData);
          }

          const materialImpactAnalysis =
            responseBody.success?.impactAnalysis?.materials ||
            defaultImpactAnalysisData;
          // `|| defaultImpactAnalysisData` falls back to a non-null object constant,
          // so materialImpactAnalysis is always truthy: the else-branch is unreachable.
          /* v8 ignore else */
          if (materialImpactAnalysis) {
            const allSubmittalDateBlock =
              responseBody.success.all_submittal_date_block;
            const formattedMaterialImpactData = getFormattedMaterialImpactData(
              materialImpactAnalysis,
              allSubmittalDateBlock
            );
            setMia(formattedMaterialImpactData);
          }

          setScheduleImpactSummary(responseBody.success?.impactSummary);
        } else {
          message.error(res?.message || ErrorMessages.SomethingWentWrong);
        }
      } catch {
        if (!controller.signal.aborted) {
          message.error(ErrorMessages.SomethingWentWrong);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      controller.abort();
    };
  }, [importLogId, sourceLogId, projectToken]);

  const submittalGridRows = useMemo(
    () => flattenSubmittalsForGrid(sia, true),
    [sia]
  );

  const materialGridRows = useMemo(
    () => flattenMaterialsForGrid(mia, true),
    [mia]
  );

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="loading-dots text-lg">
          Loading Schedule Impact Data
        </span>
      </div>
    );
  }

  return (
    <div className="bg-white m-1.5 p-2 pb-0">
      <div className="flex gap-6 px-1 pb-4 pt-1">
        <div className="w-[120px] flex">
          <AppLogoIcon />
        </div>
        <div className="text-xl font-bold">
          Schedule Impact Analysis
          {tab === "submittals" && " — Impacted Submittals"}
          {tab === "materials" && " — Impacted Materials"}
        </div>
      </div>
      <ScheduleImpactSummary scheduleImpactSummary={scheduleImpactSummary} />
      {showSubmittals && (
        <div className="mt-6">
          <h2 className="text-[14px] font-semibold mb-2 pl-4">
            Impacted Submittals ({submittalGridRows.length})
          </h2>
          <ImpactedSubmittalsGrid
            rowData={submittalGridRows}
            isLoading={false}
            printMode
          />
        </div>
      )}
      {showMaterials &&
        isAffectedMaterialEnabled &&
        materialGridRows.length > 0 && (
          <div className="mt-6">
            <h2 className="text-[14px] font-semibold mb-2 pl-4">
              Impacted Materials ({materialGridRows.length})
            </h2>
            <ImpactedMaterialsGrid
              rowData={materialGridRows}
              isLoading={false}
              printMode
            />
          </div>
        )}
      <div className="fixed bottom-5 right-5 p-4 z-40 print:hidden">
        <Button
          type="primary"
          shape="circle"
          size="large"
          onClick={() => window.print()}
          icon={<PrinterOutlined />}
          className="print:hidden"
        />
      </div>
    </div>
  );
}

export default ScheduleImpactExportPDF;
